import React, { useState, useEffect, useContext, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useParams, Navigate } from 'react-router-dom';
import { Layout, Menu, Card, Table, Tag, Button, Space, Typography, Spin, Alert, Upload, message, Form, Input, Dropdown, Checkbox, Tooltip, Modal, ConfigProvider } from 'antd';
import zhCN from 'antd/es/locale/zh_CN';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import { MenuUnfoldOutlined, MenuFoldOutlined, UploadOutlined, PaperClipOutlined, ShopOutlined, BarsOutlined, DownOutlined, LogoutOutlined, ExclamationCircleFilled, UserOutlined } from '@ant-design/icons';
import { projects, locations } from './mockData';
import GanttChart from './GanttChart';
import { AuthContext, AuthProvider } from './AuthContext';
import PrivateRoute from './PrivateRoute';
import LoginPage from './LoginPage';
import { calculateROI, parseNumericValue } from './roiCalculator';
import D1GanttPage from './D1GanttPage'; 
import CrowdPortraitPage from './CrowdPortraitPage'; 
import './Responsive.css';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

// --- New Home Page: Location Selection ---
const LocationSelectionPage = () => {
  const columns = [
    { title: '城市', dataIndex: 'city', key: 'city' },
    { title: '位置', dataIndex: 'location', key: 'location' },
    { title: '综合评分', dataIndex: 'score', key: 'score', sorter: (a, b) => a.score - b.score },
    { title: '回本周期 (月)', dataIndex: 'payback_period', key: 'payback_period', sorter: (a, b) => a.payback_period - b.payback_period },
    {
      title: '状态',
      key: 'status',
      dataIndex: 'status',
      render: (status, record) => {
        if (record.project_id) {
          return (
            <Link to={`/projects/${record.project_id}`}>
              <Tag color="processing">{status}</Tag>
            </Link>
          );
        }
        return <Tag color="default">{status}</Tag>;
      },
    },
  ];

  return (
    <Card title="网吧新店选址评估">
      <Table columns={columns} dataSource={locations} rowKey="id" scroll={{ x: 'max-content' }} />
    </Card>
  );
};

const EditableContext = React.createContext(null);

const EditableRow = ({ index, ...props }) => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

const EditableCell = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = () => {
    setEditing(!editing);
    form.setFieldsValue({
      [dataIndex]: record[dataIndex],
    });
  };

  const save = async () => {
    try {
      const values = await form.validateFields();
      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item
        style={{
          margin: 0,
        }}
        name={dataIndex}
        rules={[
          {
            required: true,
            message: `${title} is required.`,
          },
        ]}
      >
        <Input ref={inputRef} onPressEnter={save} onBlur={save} />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          paddingRight: 24,
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

// --- New Store Details Page (修复版) ---
const StoreDetailsPage = () => {
  const [modal, contextHolder] = Modal.useModal();
  const [storeDetails, setStoreDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { userRole } = useContext(AuthContext);
  const isReadOnly = userRole === 'readonly';
  
  // 1. 将 generateColumns 放在这里
  function generateColumns() {
    
    // --- 辅助函数：生成文件上传和展示的单元格 ---
    const renderDocumentCell = (docs, record, fieldName) => {
      let documents = [];
      try {
        if (typeof docs === 'string') documents = JSON.parse(docs);
        else if (Array.isArray(docs)) documents = docs;
      } catch (e) { documents = []; }

      // 删除文件逻辑
      const handleDocDelete = (docToDelete) => {
        if (isReadOnly) return;
        modal.confirm({
          title: '您确定要删除这个文件吗?',
          icon: <ExclamationCircleFilled />,
          content: `文件名: ${docToDelete.name}`,
          okText: '确认删除',
          okType: 'danger',
          cancelText: '取消',
          onOk: async () => {
            try {
              setLoading(true);
              const res = await fetch(`/api/file/${docToDelete.key}`, { method: 'DELETE' });
              if (!res.ok) throw new Error('Delete failed');

              const updatedDocs = documents.filter(doc => doc.key !== docToDelete.key);
              // 注意：这里动态更新传入的 fieldName
              handleSave({ ...record, [fieldName]: updatedDocs });
              message.success('删除成功');
            } catch (err) {
              message.error('删除失败');
            } finally {
              setLoading(false);
            }
          },
        });
      };

      // 重命名逻辑
      const handleDocRename = (docToRename, newName) => {
        const updatedDocs = documents.map(doc =>
          doc.key === docToRename.key ? { ...doc, name: newName } : doc
        );
        handleSave({ ...record, [fieldName]: updatedDocs });
      };

      return (
        <Space direction="vertical" style={{ width: '100%' }}>
          {/* 上传按钮区域 */}
          <div style={{ marginBottom: 4 }}>
             <Upload
                customRequest={(options) => handleUpload(options, record, fieldName)}
                showUploadList={false}
                multiple
                disabled={isReadOnly}
              >
                <Button icon={<UploadOutlined />} size="small" type="dashed" disabled={isReadOnly} block>
                  点击上传
                </Button>
              </Upload>
          </div>

          {/* 文件展示区域 */}
          {documents.length > 0 ? (
            <Space direction="vertical" size={2} style={{ width: '100%' }}>
              {documents.map(doc => (
                <Tag
                  key={doc.key}
                  closable={!isReadOnly}
                  onClose={(e) => { e.preventDefault(); handleDocDelete(doc); }}
                  icon={<PaperClipOutlined />}
                  color={fieldName === 'license_documents' ? 'orange' : 'blue'} // 区分颜色
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}
                >
                  <a href={`https://pub-47540e3d1c0c47f6b9505e84e0f27df6.r2.dev/${doc.key}`} target="_blank" rel="noopener noreferrer" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '120px', display: 'inline-block' }}>
                    <Typography.Text
                      style={{ color: 'inherit' }}
                      editable={!isReadOnly ? {
                        onChange: (newName) => handleDocRename(doc, newName),
                        tooltip: `原始文件名: ${doc.originalName || doc.name}`
                      } : false}
                    >
                      {doc.name}
                    </Typography.Text>
                  </a>
                </Tag>
              ))}
            </Space>
          ) : <span style={{ color: '#ccc', fontSize: '12px' }}>暂无文件</span>}
        </Space>
      );
    };

    return [
      {
        title: '序号',
        dataIndex: 'sort_order',
        key: 'sort_order',
        width: 60,
        fixed: 'left',
        align: 'center',
        render: (text, record, index) => <b>{index + 1}</b>
      },
      {
        title: '门店名称',
        dataIndex: 'store_name',
        key: 'store_name',
        width: 140,
        fixed: 'left',
        render: (text, record) => (
          <Link to={`/gantt/${record.store_id}`} style={{ fontWeight: 'bold' }}>
            {text}
          </Link>
        ),
        editable: true,
      },
      // === 调整位置：移动到门店名称之后 ===
      { 
        title: '营业执照主体', 
        dataIndex: 'license_name', 
        key: 'license_name', 
        width: 200, 
        editable: true, 
        sorter: (a, b) => (a.license_name || '').localeCompare(b.license_name || '') 
      },
      { 
        title: '合同金额', 
        dataIndex: 'contract_amount', 
        key: 'contract_amount', 
        width: 120, 
        editable: true, 
        sorter: (a, b) => (parseFloat(a.contract_amount) || 0) - (parseFloat(b.contract_amount) || 0) 
      },
      // === 拆分文件上传列 ===
      {
        title: '证照公示(营业/消防等)',
        dataIndex: 'license_documents',
        key: 'license_documents',
        width: 220,
        render: (docs, record) => renderDocumentCell(docs, record, 'license_documents')
      },
      {
        title: '合同/协议文件',
        dataIndex: 'related_documents',
        key: 'related_documents',
        width: 220,
        render: (docs, record) => renderDocumentCell(docs, record, 'related_documents')
      },
      // === 其余字段 ===
      {
        title: '快捷操作',
        key: 'actions',
        width: 120,
        fixed: 'right',
        render: (_, record) => (
          <Link to={`/crowd-portrait/${record.store_id}`}>
            <Button type="link">人群画像分析</Button>
          </Link>
        ),
      },
      { title: '详细地址', dataIndex: 'detailed_address', key: 'detailed_address', width: 250, editable: true },
      { title: '所处区域', dataIndex: 'district', key: 'district', width: 100, editable: true, sorter: (a, b) => (a.district || '').length - (b.district || '').length },
      { title: '建筑面积', dataIndex: 'building_area', key: 'building_area', width: 100, editable: true, sorter: (a, b) => (a.building_area || 0) - (b.building_area || 0) },
      { title: '套内面积', dataIndex: 'usable_area', key: 'usable_area', width: 100, editable: true, sorter: (a, b) => (a.usable_area || 0) - (b.usable_area || 0) },
      { title: '租金', dataIndex: 'rent', key: 'rent', width: 100, editable: true, sorter: (a, b) => (a.rent || 0) - (b.rent || 0) },
      { title: '免租期', dataIndex: 'rent_free_period', key: 'rent_free_period', width: 100, editable: true, sorter: (a, b) => (a.rent_free_period || 0) - (b.rent_free_period || 0) },
      { title: '物业费', dataIndex: 'property_fee', key: 'property_fee', width: 80, editable: true, sorter: (a, b) => (a.property_fee || 0) - (b.property_fee || 0) },
      { title: '电费', dataIndex: 'electricity_fee', key: 'electricity_fee', width: 80, editable: true, sorter: (a, b) => (a.electricity_fee || 0) - (b.electricity_fee || 0) },
      { title: '水费', dataIndex: 'water_fee', key: 'water_fee', width: 80, editable: true, sorter: (a, b) => (a.water_fee || 0) - (b.water_fee || 0) },
      { title: '付款方式', dataIndex: 'payment_method', key: 'payment_method', width: 100, editable: true, sorter: (a, b) => (a.payment_method || '').length - (b.payment_method || '').length },
      { title: '租金递增', dataIndex: 'rent_increase', key: 'rent_increase', width: 120, editable: true, sorter: (a, b) => (a.rent_increase || '').length - (b.rent_increase || '').length },
      { title: '合同年限', dataIndex: 'contract_years', key: 'contract_years', width: 100, editable: true, sorter: (a, b) => (a.contract_years || 0) - (b.contract_years || 0) },
      { title: '门店属性', dataIndex: 'properties', key: 'properties', width: 150, editable: true, sorter: (a, b) => (a.properties || '').length - (b.properties || '').length },
      { title: '开办杂费', dataIndex: 'startup_costs', key: 'startup_costs', width: 150, editable: true, sorter: (a, b) => (a.startup_costs || 0) - (b.startup_costs || 0) },
      { title: '筹开进度', dataIndex: 'progress', key: 'progress', width: 200, editable: true, sorter: (a, b) => (a.progress || '').length - (b.progress || '').length },
      { title: '回本周期', dataIndex: 'roi_period', key: 'roi_period', width: 100, editable: true, sorter: (a, b) => (a.roi_period || 0) - (b.roi_period || 0) },
      {
        title: '回报率测算(月)',
        key: 'roi_calculation',
        width: 140,
        align: 'center',
        render: (text, record) => {
          const grossArea = parseNumericValue(record.building_area);
          const usableArea = parseNumericValue(record.usable_area);
          const totalRent = parseNumericValue(record.rent);
          const propFeePerSqm = parseNumericValue(record.property_fee);
          const startupCosts = parseNumericValue(record.startup_costs);
          
          const rentPerSqm = (grossArea > 0) ? totalRent / grossArea : 0;

          const inputs = {
            gross_area: grossArea,
            net_area: usableArea,
            rent_per_sqm: rentPerSqm,
            prop_fee_per_sqm: propFeePerSqm,
            misc_startup_fee: startupCosts,
          };

          const results = calculateROI(inputs);

          const renderTag = () => {
            if (results.error) return <Tag color="error">计算错误</Tag>;
            if (results.payback_period === '亏损或无利润') return <Tag color="red">{results.payback_period}</Tag>;
            const period = parseFloat(results.payback_period);
            let color = 'default';
            if (period <= 12) color = 'success';
            else if (period <= 24) color = 'blue';
            else color = 'warning';
            return <Tag color={color}>{`${results.payback_period} 月`}</Tag>;
          };
          
          const tag = renderTag();
          if (results.warnings && results.warnings.length > 0) {
            const tooltipContent = (
              <div>
                <strong>计算提示:</strong>
                {results.warnings.map((warning, i) => <div key={i}>- {warning}</div>)}
              </div>
            );
            return <Tooltip title={tooltipContent}>{tag}</Tooltip>;
          }
          return tag;
        }
      },
    ];
  }

  // 2. 初始化 visibleColumnKeys
  const [visibleColumnKeys, setVisibleColumnKeys] = useState(() => generateColumns().map(c => c.key));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/store-details');
      if (!response.ok) throw new Error(`Network response was not ok: ${response.statusText}`);
      const data = await response.json();

      if (Array.isArray(data.storeDetails)) {
        const processedDetails = data.storeDetails.map(item => {
          // 解析 contract/related documents
          let relatedDocs = [];
          try {
            if (typeof item.related_documents === 'string') relatedDocs = JSON.parse(item.related_documents);
            else if (Array.isArray(item.related_documents)) relatedDocs = item.related_documents;
          } catch (e) { console.error("Failed to parse related_documents", item); }
          
          // 解析 license documents (新增)
          let licenseDocs = [];
          try {
            if (typeof item.license_documents === 'string') licenseDocs = JSON.parse(item.license_documents);
            else if (Array.isArray(item.license_documents)) licenseDocs = item.license_documents;
          } catch (e) { console.error("Failed to parse license_documents", item); }

          return { 
            ...item, 
            key: item.store_id, 
            related_documents: relatedDocs,
            license_documents: licenseDocs // 确保放入 state
          };
        });
        setStoreDetails(processedDetails);
      }
    } catch (error) {
      setError('数据加载失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleRefresh = () => fetchData();

  const handleSave = async (row) => {
    const newData = [...storeDetails];
    const index = newData.findIndex((item) => row.key === item.key);
    
    if (index > -1) {
      const item = newData[index];
      newData.splice(index, 1, { ...item, ...row });
      setStoreDetails(newData);

      const { key, ...rest } = row; 
      const dataToSave = { ...rest };

      // 序列化 related_documents
      if (Array.isArray(dataToSave.related_documents)) {
        dataToSave.related_documents = JSON.stringify(dataToSave.related_documents);
      } else {
        dataToSave.related_documents = dataToSave.related_documents || '[]';
      }

      // 序列化 license_documents (新增)
      if (Array.isArray(dataToSave.license_documents)) {
        dataToSave.license_documents = JSON.stringify(dataToSave.license_documents);
      } else {
        dataToSave.license_documents = dataToSave.license_documents || '[]';
      }

      try {
        const response = await fetch(`/api/store-detail/${row.store_id}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dataToSave),
        });
        if (!response.ok) throw new Error('Save failed');
        message.success('保存成功');
      } catch (err) {
        message.error('保存失败，回滚中...');
        newData.splice(index, 1, item);
        setStoreDetails(newData);
      }
    }
  };

  // 修改 handleUpload 以支持 fieldName 参数
  const handleUpload = async (options, record, fieldName) => {
    const { file, onSuccess, onError } = options;
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);
      const res = await fetch('/api/upload', { method: 'POST', body: formData });
      if (!res.ok) throw new Error('Upload failed');

      const newFileData = await res.json();
      onSuccess(newFileData);
      message.success(`${file.name} 上传成功`);

      // 动态获取当前列的文档列表
      const currentDocs = Array.isArray(record[fieldName]) ? record[fieldName] : [];
      const updatedDocs = [...currentDocs, {
        name: newFileData.name.substring(0, 5),
        key: newFileData.key,
        originalName: newFileData.name
      }];

      // 保存到指定字段
      handleSave({ ...record, [fieldName]: updatedDocs });

    } catch (err) {
      onError(err);
      message.error('上传失败');
    } finally {
      setLoading(false);
    }
  };

  const components = {
    body: {
      row: EditableRow,
      cell: EditableCell,
    },
  };

  const allColumnsCurrent = generateColumns(); 
  const fixedColumns = allColumnsCurrent.filter(c => c.fixed === 'left');
  const scrollableColumns = allColumnsCurrent.filter(c => c.fixed !== 'left' && visibleColumnKeys.includes(c.key));

  const columns = [...fixedColumns, ...scrollableColumns].map((col) => {
    if (!col.editable) return col;
    return {
      ...col,
      onCell: (record) => ({
        record,
        editable: isReadOnly ? false : col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  return (
    <>
      <Card
        title="门店详情列表"
        extra={
        <Space>
          <Dropdown menu={{
              items: [
                {
                  key: '1',
                  label: (
                    <Checkbox.Group
                      options={allColumnsCurrent.filter(c => c.fixed !== 'left').map(c => ({ label: c.title, value: c.key }))}
                      value={visibleColumnKeys}
                      onChange={setVisibleColumnKeys}
                      style={{ display: 'flex', flexDirection: 'column', padding: 10 }}
                    />
                  )
                }
              ],
              onClick: e => e.stopPropagation(),
            }} trigger={['click']}>
            <Button>自定义列 <DownOutlined /></Button>
          </Dropdown>
          <Button type="primary" disabled>批量导出</Button>
          <Button onClick={handleRefresh} disabled={loading} loading={loading}>
            {loading ? '刷新中' : '刷新数据'}
          </Button>
        </Space>
      }
    >
      {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 16 }} />}
      <Spin spinning={loading}>
        <Table
          components={components}
          rowClassName={() => 'editable-row'}
          bordered
          dataSource={storeDetails}
          columns={columns}
          rowKey="key"
          scroll={{ x: 2600 }} // 稍微调宽一点scroll
          pagination={{ defaultPageSize: 10 }}
          size="middle"
        />
      </Spin>
    </Card>
    {contextHolder}
    </>
  );
};

// --- Project Detail Page (with ViewMode switcher) ---
const ProjectPage = () => {
  const { projectId } = useParams();
  const project = projects.find(p => p.id === parseInt(projectId));
  
  if (!project) {
    return <h2>项目未找到！</h2>;
  }

  return (
    <div>
      <Title level={2}>{project.name}</Title>
      <p>{project.description}</p>
      
      <div style={{ marginTop: 24, height: '600px', width: '100%' }}>
        <GanttChart />
      </div>
    </div>
  );
};

// --- Main App Layout ---
function App() {
  const [collapsed, setCollapsed] = useState(false);
  const [stores, setStores] = useState([]);
  const { logout, userRole } = useContext(AuthContext);

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin': return '超级管理员';
      case 'readonly': return '只读用户';
      default: return role; // 部门名本身就是中文，直接返回
    }
  };

  useEffect(() => {
    // Fetch store list for the menu
    const fetchStores = async () => {
      try {
        const response = await fetch('/api/store-details');
        if (!response.ok) {
          throw new Error(`Failed to fetch stores: ${response.statusText}`);
        }
        const text = await response.text();
        try {
          const data = JSON.parse(text);
          if (Array.isArray(data.storeDetails)) {
            setStores(data.storeDetails);
          }
        } catch (e) {
          console.error("Failed to parse JSON for store list:", text);
        }
      } catch (error) {
        console.error("Failed to fetch stores for menu:", error);
      }
    };
    fetchStores();
  }, []);

  const menuItems = [
    {
      key: 'store-management',
      icon: <ShopOutlined />,
      label: '门店管理',
      children: [
        {
          key: 'store-details',
          icon: <BarsOutlined />,
          label: <Link to="/store-details">全部门店详情</Link>,
        },
        ...stores.map(store => ({
          key: store.store_id,
          label: <Link to={`/gantt/${store.store_id}`}>{store.store_name}</Link>,
        })),
      ],
    },
  ];

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={(value) => setCollapsed(value)}
        breakpoint="lg"
        collapsedWidth="0"
      >
        <div style={{ height: '32px', margin: '16px', background: 'rgba(255, 255, 255, 0.2)', textAlign: 'center', lineHeight: '32px', color: 'white', borderRadius: '4px' }}>
          {collapsed ? 'SaaS' : '网吧项目管理'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          defaultOpenKeys={['store-management']}
          defaultSelectedKeys={['store-details']}
          items={menuItems}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{display: 'flex', alignItems: 'center'}}>
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed),
              })}
              <div style={{ fontSize: '18px', fontWeight: 'bold', marginLeft: '10px' }}>内部工作联通系统</div>
            </div>
            <Space size="large">
              <div style={{ color: '#666' }}>
                <UserOutlined style={{ marginRight: 4 }} />
                当前身份：<Tag color="blue">{getRoleLabel(userRole)}</Tag>
              </div>
              <Button icon={<LogoutOutlined />} onClick={logout}>
                退出登录
              </Button>
            </Space>
        </Header>
        
        {/* --- Content Area --- */}
        <Content style={{
          margin: '24px 16px',
          padding: 24,
          background: '#fff',
          overflowX: 'auto'
        }}>
          <Routes>
            <Route path="/store-details" element={<StoreDetailsPage />} />
            <Route path="/" element={<Navigate to="/store-details" replace />} />
            <Route path="/projects/:projectId" element={<ProjectPage />} />
            <Route path="/gantt/:storeId" element={<D1GanttPage />} />
            <Route path="/crowd-portrait/:storeId" element={<CrowdPortraitPage />} />
            <Route path="/location-selection" element={<LocationSelectionPage />} />
          </Routes>
        </Content>
        
      </Layout>
    </Layout>
  );
}

// --- App Wrapper ---
const AppWrapper = () => (
  <ConfigProvider locale={zhCN}>
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/*" element={
            <PrivateRoute>
              <App />
            </PrivateRoute>
          } />
        </Routes>
      </AuthProvider>
      <div id="live-demo-additional-container" style={{ position: 'fixed', top: 0, left: 0, zIndex: 10000 }} />
    </Router>
  </ConfigProvider>
);

export default AppWrapper;
